/**
 * Utils - lightweight helper functions used across Genesys modules.
 */

import { UNDEFINED_VALUE } from '../const.js';

/**
 * Ensures a condition is true, logging a stack trace if it's not.
 *
 * @param condition - The condition to check
 * @param message - Optional message to log if condition is false
 * @returns The original condition value
 */
export function ensure(condition: any, message?: string): boolean {
  if (!Boolean(condition)) {
    message ??= 'ensure failed';
    console.error(new Error(message));
  }
  return condition;
}

/**
 * Checks a condition, throwing an error if it's false.
 *
 * @param condition - The condition to check
 * @param message - Optional message to throw if condition is false
 */
export function check(condition: any, message?: string) {
  if (!Boolean(condition)) {
    message ??= 'check failed';
    throw new Error(message);
  }
}

/**
 * Sleeps for a given number of milliseconds.
 *
 * @param ms - The number of milliseconds to sleep
 * @returns A promise that resolves after the given number of milliseconds
 */
export async function sleep(ms: number) {
  await new Promise(resolve => setTimeout(resolve, ms));
}

export type OptionalSome<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Utility type that excludes properties whose value types are functions.
 * Used to prevent passing methods/callbacks in initialization options.
 *
 * @example
 * ```ts
 * interface MyOptions {
 *   name: string;
 *   onComplete: () => void;  // This will be excluded
 * }
 *
 * type SafeOptions = ExcludeFunctionFields<MyOptions>;
 * // Result: { name: string }
 * ```
 */
export type ExcludeFunctionFields<T> = {
  [K in keyof T as NonNullable<T[K]> extends (...args: any[]) => any ? never : K]: T[K];
};


export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.document !== 'undefined';
}

export function isNode(): boolean {
  return typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
}


export function omitIfDefault<T>(current: T, defaultValue: T): T | undefined {
  // Check if objects have equals method and use it for comparison
  if (current && typeof current === 'object' && 'equals' in current && typeof current.equals === 'function') {
    return current.equals(defaultValue) ? undefined : current;
  }
  return current === defaultValue ? undefined : current;
}

export function overrideIfDefault<T>(current: T, defaultValue: T, override: T): T {
  // Check if objects have equals method and use it for comparison
  if (current && typeof current === 'object' && 'equals' in current && typeof current.equals === 'function') {
    return current.equals(defaultValue) ? override : current;
  }
  return current === defaultValue ? override : current;
}

export function isMobileBrowser(): boolean {
  if (!isBrowser()) {
    return false;
  }
  // 1) UA sniff as a fallback
  const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (uaMobile) {
    console.log(`Returning true from isMobileBrowser because of user agent "${navigator.userAgent}"`);
    return true;
  }

  // 2) Touch-based feature detection
  const hasTouch = 'ontouchstart' in window;
  if (hasTouch) {
    console.log(`Returning true from isMobileBrowser because of touch capability, hasTouch: ${hasTouch}`);
    return true;
  }

  return false;
}

export function getEnv(name: string): any | undefined {
  return import.meta.env && import.meta.env[name];
}

/**
 * Strips the UTF-8 Byte Order Mark (BOM) from the beginning of a string if present.
 *
 * @param text - The string that may contain a BOM
 * @returns The string with the BOM removed
 */
export function stripBom(text: string): string {
  return text.replace(/^\uFEFF/, '');
}

/**
 * Parses JSON text after automatically stripping any BOM header.
 *
 * @param text - The JSON string that may contain a BOM
 * @returns The parsed JSON value
 */
export function parseJson<T = any>(text: string): T {
  return JSON.parse(stripBom(text)) as T;
}

/**
 * Deep merges two objects, recursively merging nested objects.
 *
 * @param target - The target object to merge into
 * @param source - The source object to merge from
 * @returns The merged object
 */
export function deepMerge<T extends Record<string, any>, U extends Record<string, any>>(
  target: T,
  source: U
): T & U {
  const result = { ...target } as T & U;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key as keyof typeof result];

      // Skip undefined source values to preserve target values
      if (sourceValue === undefined || sourceValue === UNDEFINED_VALUE) {
        continue;
      }

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        targetValue &&
        typeof targetValue === 'object'
      ) {
        if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
          // Both are arrays, merge elements by index
          const mergedArray = [...targetValue];
          for (let i = 0; i < sourceValue.length; i++) {
            if (i < targetValue.length &&
              sourceValue[i] &&
              typeof sourceValue[i] === 'object' &&
              targetValue[i] &&
              typeof targetValue[i] === 'object') {
              // Both elements are objects, deep merge them
              mergedArray[i] = deepMerge(targetValue[i], sourceValue[i]);
            } else {
              if (sourceValue[i] === undefined || sourceValue[i] === UNDEFINED_VALUE) {
                continue;
              }
              // Use source element (replace or extend array)
              mergedArray[i] = sourceValue[i];
            }
          }
          result[key as keyof typeof result] = mergedArray as any;
        } else if (!Array.isArray(sourceValue) && !Array.isArray(targetValue)) {
          // Both are objects, recursively merge
          result[key as keyof typeof result] = deepMerge(targetValue, sourceValue) as any;
        } else {
          // One is array, one is object - source overwrites
          result[key as keyof typeof result] = sourceValue as any;
        }
      } else {
        // Source value overwrites target value
        result[key as keyof typeof result] = sourceValue as any;
      }
    }
  }

  return result;
}


/**
 * Creates a diff object containing only the properties that are different between source and target.
 *
 * @param target - The target object to compare against
 * @param source - The source object to extract differences from
 * @returns An object containing only the properties that differ from target
 */
export function deepDiff<T extends Record<string, any>, U extends Record<string, any>>(
  target: T,
  source: U
): Partial<U> {
  const result: any = {};

  // If target is not an object, return the entire source
  if (!target || typeof target !== 'object') {
    return source;
  }

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = (target as any)[key];

      // If target doesn't have this key, include it in diff
      if (!(key in target)) {
        result[key] = sourceValue;
        continue;
      }

      // If values are strictly equal, skip
      if (sourceValue === targetValue) {
        continue;
      }

      // Handle null/undefined cases
      if (sourceValue == null || targetValue == null) {
        if (sourceValue !== targetValue) {
          result[key] = sourceValue;
        }
        continue;
      }

      // Both are objects/arrays
      if (typeof sourceValue === 'object' && typeof targetValue === 'object') {
        if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
          // Arrays - check if they're different
          if (sourceValue.length !== targetValue.length) {
            result[key] = sourceValue;
          } else {
            // Check each element
            let arrayDifferent = false;
            const arrayDiff: any[] = [];

            for (let i = 0; i < sourceValue.length; i++) {
              if (sourceValue[i] && typeof sourceValue[i] === 'object' &&
                targetValue[i] && typeof targetValue[i] === 'object') {
                const elementDiff = deepDiff(targetValue[i], sourceValue[i]);
                if (Object.keys(elementDiff).length > 0) {
                  arrayDiff[i] = elementDiff;
                  arrayDifferent = true;
                } else {
                  arrayDiff[i] = UNDEFINED_VALUE;
                }
              } else if (sourceValue[i] !== targetValue[i]) {
                arrayDiff[i] = sourceValue[i];
                arrayDifferent = true;
              } else {
                arrayDiff[i] = UNDEFINED_VALUE;
              }
            }

            if (arrayDifferent) {
              result[key] = arrayDiff;
            }
          }
        } else if (!Array.isArray(sourceValue) && !Array.isArray(targetValue)) {
          // Both are objects
          const objectDiff = deepDiff(targetValue, sourceValue);
          if (Object.keys(objectDiff).length > 0) {
            result[key] = objectDiff;
          }
        } else {
          // One is array, one is object - they're different
          result[key] = sourceValue;
        }
      } else {
        // Primitive values that are different
        result[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Deep removes empty objects ({}) from an object recursively.
 *
 * @param obj - The object to clean
 * @returns The object with empty objects removed
 */
export function deepRemoveEmptyObjects<T extends Record<string, any>>(obj: T): T {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return obj;
  }

  const result: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively clean nested objects
        const cleaned = deepRemoveEmptyObjects(value);

        // Only add if the cleaned object has properties
        if (Object.keys(cleaned).length > 0) {
          result[key] = cleaned;
        }
      } else {
        // Keep non-object values and arrays
        if (value !== undefined) {
          result[key] = value;
        }
      }
    }
  }

  return result;
}

